import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { XCircle, Loader2, ArrowRight, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import AmbientStarfield from "../components/rewards/shared/AmbientStarfield";
import SacredEmblem from "../components/rewards/shared/SacredEmblem";
import CoinReveal from "../components/rewards/CoinReveal";

const POLL_ATTEMPTS = 25;   // ~30s ceiling — gives the webhook time to credit
const POLL_INTERVAL_MS = 1200;
// After N polls with Stripe reporting payment_status=paid, stop gating the
// modal transition on the client-side balance-match. The webhook is the
// authoritative source of the credit; a slow /credits/balance response
// (or a missed poll response) shouldn't keep the user staring at a spinner.
const PAID_FAST_PATH_MIN_POLLS = 2;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const type = params.get("type") || "coin_pack";

  const [status, setStatus] = useState("polling");   // polling | paid | timeout | failed
  const [payment, setPayment] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [preBalance, setPreBalance] = useState(null); // balance BEFORE the top-up
  const [creditLanded, setCreditLanded] = useState(false);

  const check = useCallback(async () => {
    if (!sessionId) { setStatus("failed"); return; }
    try {
      const [statusRes, balanceRes] = await Promise.all([
        authAxios.get(`/stripe/status/${sessionId}`).catch((err) => {
          // Also swallow status errors softly so the fast-path fallback still
          // fires on the 2nd attempt when Stripe is slow.
          console.warn("[PaymentSuccess] /stripe/status fetch failed:", err?.message || err);
          return null;
        }),
        authAxios.get(`/credits/balance`).catch((err) => {
          // Expose the swallowed failure so dev-tools shows why the CoinReveal
          // fell back to the plain "Payment confirmed" screen instead of the
          // animated reveal.
          console.warn("[PaymentSuccess] /credits/balance fetch failed:", err?.message || err);
          return null;
        }),
      ]);
      const stripePayment = statusRes.data;
      setPayment(stripePayment);
      const topUp = Number(stripePayment?.base_coins || 0) + Number(stripePayment?.bonus_coins || 0);
      const subscribeBonus = Number(stripePayment?.subscribe_bonus_coins || 0);
      const totalCredited = topUp + subscribeBonus;
      const currBal = balanceRes?.data?.balance != null ? Number(balanceRes.data.balance) : null;

      // Latch the pre-topup balance on the first poll where we have signal.
      // Prefer the authoritative `pre_balance` from /stripe/status (derived
      // from the coin_ledger PURCHASE row's balance_after) — this handles
      // negative starting balances correctly. Fall back to inferring from
      // /credits/balance − totalCredited only if the ledger row hasn't
      // materialised yet.
      setPreBalance((prev) => {
        if (prev != null) return prev;
        if (stripePayment?.pre_balance != null) return Number(stripePayment.pre_balance);
        if (currBal == null) return prev;
        if (stripePayment?.payment_status !== "paid") return currBal;
        if (totalCredited > 0) return currBal - totalCredited; // allow negative
        return currBal;
      });

      // Detect that ALL credits have landed. The backend reporting
      // pre_balance means the PURCHASE ledger row exists → credit landed.
      if (stripePayment?.pre_balance != null && stripePayment?.post_balance != null) {
        setCreditLanded(true);
      } else if (currBal != null && totalCredited > 0 && preBalance != null) {
        if (currBal >= preBalance + totalCredited) setCreditLanded(true);
      } else if (currBal != null && totalCredited > 0 && preBalance == null && stripePayment?.payment_status === "paid" && currBal >= totalCredited) {
        setCreditLanded(true);
      }

      // Trust the webhook after PAID_FAST_PATH_MIN_POLLS polls even if the
      // client-side balance match hasn't converged (e.g. /credits/balance
      // returning stale data, error, or the account is mid-usage).
      const isPaid = stripePayment?.payment_status === "paid";
      const balanceMatched = creditLanded || totalCredited === 0;
      const fastPathOpen = attempts >= PAID_FAST_PATH_MIN_POLLS;
      if (isPaid && (balanceMatched || fastPathOpen)) {
        setStatus("paid");
        toast.success("Payment received");
      } else {
        setAttempts((a) => a + 1);
      }
    } catch (err) {
      console.error("payment status poll failed:", err);
      setAttempts((a) => a + 1);
    }
  }, [sessionId, preBalance, creditLanded, attempts]);

  useEffect(() => {
    if (status !== "polling") return;
    if (attempts >= POLL_ATTEMPTS) { setStatus("timeout"); return; }
    const t = setTimeout(check, attempts === 0 ? 0 : POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [status, attempts, check]);

  const goContinue = () => {
    let target = "/dashboard";
    try {
      const saved = sessionStorage.getItem("agent_eros_payment_return_to");
      if (saved && saved.startsWith("/") && !saved.startsWith("/payment/")) {
        target = saved;
      }
      sessionStorage.removeItem("agent_eros_payment_return_to");
    } catch (_) { /* storage disabled — fall back to /dashboard */ }
    navigate(target);
  };

  // Build CoinReveal claims: "CURRENT BALANCE" (before top-up) + top-up row,
  // plus a "SUBSCRIBE BONUS" row when a sponsor/system Subscribe offer was
  // credited alongside the checkout (surfaced via /stripe/status).
  const buildClaims = () => {
    if (!payment) return [];
    const base = Number(payment.base_coins || 0);
    const bonus = Number(payment.bonus_coins || 0);
    const subscribeBonus = Number(payment.subscribe_bonus_coins || 0);
    const topUp = base + bonus;
    if (topUp <= 0 && subscribeBonus <= 0) return [];
    // Do NOT clamp negative pre-balances to 0 — if the user was on
    // credit-runway (negative balance from prior LLM usage) the reveal
    // should show the actual starting number so the NEW BALANCE math is
    // internally consistent.
    const currentBalance = preBalance ?? 0;
    const purchaseType = String(payment.purchase_type || "");
    // Subscriptions always credit in monthly slices — yearly is only a billing
    // cycle (paid upfront with a discount, coins drip in monthly). Always show
    // "MONTHLY TOP UP" for subscription reveals.
    const topUpLabel = purchaseType === "subscription" ? "MONTHLY TOP UP" : "TOP UP";
    const rows = [
      { label: "CURRENT BALANCE", amount: currentBalance },
      { label: topUpLabel, amount: topUp },
    ];
    if (subscribeBonus > 0) {
      rows.push({ label: "SUBSCRIBE BONUS", amount: subscribeBonus });
    }
    return rows;
  };

  const claims = status === "paid" ? buildClaims() : [];

  return (
    <div className="min-h-screen bg-[#050510] relative flex items-center justify-center px-4 py-10">
      <AmbientStarfield />
      <div className="relative z-10 max-w-md w-full min-h-[700px] rounded-2xl border border-divine-gold/25 bg-black/50 backdrop-blur-xl p-8 pb-10 text-center flex flex-col" data-testid="payment-success-card">
        <div className="flex justify-center mb-2">
          <SacredEmblem size={140} className="opacity-90" />
        </div>

        {status === "polling" && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <Loader2 className="w-10 h-10 text-divine-gold animate-spin mb-4" />
            <h1 className="text-xl font-semibold text-divine-gold mb-2">Confirming your payment…</h1>
            <p className="text-white/60 text-sm">Just a moment while Stripe finalises the charge.</p>
            <p className="text-white/30 text-xs mt-3">Attempt {attempts + 1} of {POLL_ATTEMPTS}</p>
          </div>
        )}

        {status === "paid" && (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-semibold text-divine-gold mb-4" data-testid="payment-success-heading">
              Payment Received
            </h1>

            {claims.length > 0 ? (
              <div className="flex-1 flex flex-col justify-between mt-2" data-testid="payment-success-coin-reveal">
                <CoinReveal
                  claims={claims}
                  onClaim={goContinue}
                  claimLabel="CONTINUE"
                  claimSubtext=""
                  subtitle=""
                  replayable={true}
                  soundEnabled={true}
                  totalLabel="NEW BALANCE"
                  autoStart={true}
                />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center">
                <p className="text-white/70 text-sm mb-6">Your payment has been confirmed.</p>
                <Button
                  onClick={goContinue}
                  className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium w-full"
                  data-testid="payment-success-continue-btn"
                >
                  CONTINUE <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {(status === "failed" || status === "timeout") && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <XCircle className="w-14 h-14 text-yellow-400 mb-4" />
            <h1 className="text-xl font-semibold text-divine-gold mb-2">
              {status === "failed" ? "We couldn't verify the payment" : "Still processing…"}
            </h1>
            <p className="text-white/70 text-sm mb-6">
              {status === "timeout"
                ? "Stripe is taking longer than usual. Your coins will be credited automatically once the payment settles — no action required."
                : "Your card may not have been charged. If you were charged and don't see your coins in a minute, contact support and quote the session id below."}
            </p>
            {sessionId && (
              <p className="text-white/30 text-[10px] font-mono break-all mb-4" data-testid="session-id-echo">
                {sessionId}
              </p>
            )}
            <Button
              onClick={goContinue}
              className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium w-full"
              data-testid="payment-success-continue-btn"
            >
              <Home className="w-4 h-4 mr-2" /> CONTINUE
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
